import { authClient } from './authClient';
import { useAuthStore } from '../store/useAuthStore';
import { useTeamStore } from '../store/useTeamStore';
import { useChatStore } from '../store/useChatStore';
import { api } from './api';
import type { User } from './authClient';

export async function hydrateFromServer(user: User) {
  useAuthStore.getState().setUser(user);

  // SAFETY NET: if localStorage belongs to a different user, clear before hydrating.
  // For the anonymous→first-login case, lastSyncedUserId is null, so no clearing
  // happens and the normal bidirectional sync migrates the anonymous data up.
  const storedUserId = useTeamStore.getState().lastSyncedUserId;
  if (storedUserId !== null && storedUserId !== user.id) {
    useTeamStore.getState().clearLocal();
    useChatStore.getState().clearLocal();
  }

  try {
    const [serverMembers, serverSessions, me] = await Promise.all([
      api.team.list(),
      api.sessions.list(),
      api.me.get().catch(() => ({ isOperator: false, hasImplicitAccess: false, profile: { lifeContext: '', workContext: '', currentFocus: '' } })),
    ]);

    useAuthStore.getState().setIsOperator(me.isOperator);
    useAuthStore.getState().setHasImplicitAccess(me.hasImplicitAccess);
    useAuthStore.getState().setProfile(me.profile);

    const localState = useTeamStore.getState();
    const localMembers = localState.members;
    const localSessions = useChatStore.getState().sessions;

    // Bidirectional merge by ID: push what the server is missing, pull what local is missing
    const serverMemberIds = new Set(serverMembers.map((m) => m.id));
    const serverSessionIds = new Set(serverSessions.map((s) => s.id));

    const localOnlyMembers = localMembers.filter((m) => !serverMemberIds.has(m.id));
    const localOnlySessions = localSessions.filter((s) => !serverSessionIds.has(s.id));

    if (localOnlyMembers.length > 0 || localOnlySessions.length > 0) {
      await api.sync.push({ members: localOnlyMembers, sessions: localOnlySessions });
    }

    // For members on both sides: preserve local avatarImageUrl/appearanceDescription if server lacks them
    const mergedServerMembers = serverMembers.map((s) => {
      const local = localMembers.find((l) => l.id === s.id);
      return {
        ...s,
        avatarImageUrl: s.avatarImageUrl ?? local?.avatarImageUrl,
        appearanceDescription: s.appearanceDescription ?? local?.appearanceDescription,
      };
    });
    useTeamStore.setState({ members: [...mergedServerMembers, ...localOnlyMembers] });
    useChatStore.setState({ sessions: [...serverSessions, ...localOnlySessions] });

    // Mark this localStorage as belonging to this user
    useTeamStore.getState().setLastSyncedUserId(user.id);
  } catch {
    // API offline — localStorage data stays as fallback; don't update lastSyncedUserId
  }
}

export async function bootstrapSession() {
  const { data: session } = await authClient.getSession().catch(() => ({ data: null }));
  if (session?.user) {
    await hydrateFromServer(session.user);
  }
  useAuthStore.getState().setLoading(false);
}
